import React, { useState } from 'react';
import { Star, MapPin, MessageSquare, Video, Clock, Heart, Share2, Award, ChevronDown, Globe, Zap, GraduationCap, ShieldCheck, Crown, Gem, BadgeCheck, Users, ArrowRight, ShoppingBag, FileText, PlayCircle, BookOpen, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Rating } from '../components/ui/Rating';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { useCart } from '../contexts/CartContext';
import { useAstrologerProfile } from '../contexts/AstrologerProfileContext';
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';
import { AdSlot } from '../components/ads/AdSlot';
interface AstrologerProfilePageProps {
  onNavigate: (page: string) => void;
}
function BadgePill({
  badge
}: {
  badge: string;
}) {
  let icon: React.ReactNode, text: string, cls: string, tooltip: string;
  if (badge === 'TOP') {
    icon = <Crown className="w-2.5 h-2.5" data-id="element-21040" />;
    text = 'TOP';
    cls = 'bg-amber-100 text-amber-700 border-amber-200';
    tooltip = 'TOP — лучший рейтинг и отзывы';
  } else if (badge === 'PRO') {
    icon = <ShieldCheck className="w-2.5 h-2.5" data-id="element-21041" />;
    text = 'PRO';
    cls = 'bg-indigo-100 text-indigo-700 border-indigo-200';
    tooltip = 'PRO — подтверждённый профессионал';
  } else {
    icon = <Gem className="w-2.5 h-2.5" data-id="element-21042" />;
    text = 'Рек.';
    cls = 'bg-emerald-100 text-emerald-700 border-emerald-200';
    tooltip = 'Рекомендуем — выбор редакции';
  }
  return <div className="relative group/pill inline-flex" data-id="element-21043">
      <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full border cursor-help ${cls}`} data-id="element-21044">
        {icon}
        {text}
      </span>
      <div className="hidden sm:block absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-slate-800 text-white text-[10px] font-medium rounded-md whitespace-nowrap opacity-0 invisible group-hover/pill:opacity-100 group-hover/pill:visible transition-all duration-150 pointer-events-none z-50 shadow-lg" data-id="element-21045">
        {tooltip}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-21046" />
      </div>
    </div>;
}
function ReviewText({
  text,
  maxLength = 150
}: {
  text: string;
  maxLength?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  if (text.length <= maxLength) return <p className="text-slate-600 text-sm leading-relaxed" data-id="element-21047">{text}</p>;
  return <p className="text-slate-600 text-sm leading-relaxed" data-id="element-21048">
      {expanded ? text : text.slice(0, maxLength) + '...'}
      <button onClick={e => {
      e.stopPropagation();
      setExpanded(!expanded);
    }} className="ml-1 text-indigo-600 hover:text-indigo-700 font-medium text-xs" data-id="element-21049">
        {expanded ? 'Свернуть' : 'Читать далее'}
      </button>
    </p>;
}
export function AstrologerProfilePage({
  onNavigate
}: AstrologerProfilePageProps) {
  const {
    addItem
  } = useCart();
  const {
    profile
  } = useAstrologerProfile();
  const profileName = `${profile.firstName} ${profile.lastName}`;
  const profileInitials = `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`;
  const [isFavorite, setIsFavorite] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [showAllServices, setShowAllServices] = useState(false);
  const [selectedDay, setSelectedDay] = useState(1);
  const [selectedTime, setSelectedTime] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(9);
  const [currentYear, setCurrentYear] = useState(2025);
  const [shareToast, setShareToast] = useState(false);
  const monthNames = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  const dayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
    setSelectedDay(0);
  };
  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
    setSelectedDay(0);
  };
  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
    }
    setShareToast(true);
    setTimeout(() => setShareToast(false), 2000);
  };
  const baseDayNum = 14;
  const availableDays = dayNames.map((name, i) => ({
    label: name,
    num: baseDayNum + i
  }));
  const timeSlots = ['10:00', '12:00', '14:00', '16:00', '18:00', '20:00'];
  const services = [{
    title: 'Натальная карта',
    price: 'от 3 900 ₽',
    duration: '60 мин',
    format: 'video' as const,
    desc: 'Полный анализ карты: сильные стороны, предназначение, финансы и отношения.'
  }, {
    title: 'Соляр на год',
    price: 'от 4 400 ₽',
    duration: '3–5 дней',
    format: 'chat' as const,
    desc: 'Текстовый прогноз на год с разбором ключевых сфер жизни.'
  }, {
    title: 'Совместимость (Синастрия)',
    price: 'от 4 900 ₽',
    duration: '90 мин',
    format: 'video' as const,
    desc: 'Анализ совместимости партнёров, точки роста и рекомендации.'
  }, {
    title: 'Прогноз транзитов',
    price: 'от 3 900 ₽',
    duration: '3 дня',
    format: 'chat' as const,
    desc: 'Персональный прогноз на 3–12 месяцев по ключевым сферам.'
  }, {
    title: 'Видео-консультация',
    price: 'от 2 990 ₽',
    duration: '60 мин',
    format: 'video' as const,
    desc: 'Живой разбор любого вопроса: карьера, отношения, переезд, здоровье.'
  }];
  const products = [{
    id: 1,
    title: 'Гид «Ретроградный Меркурий 2025»',
    type: 'PDF-гайд',
    price: '1 490 ₽',
    icon: FileText,
    color: 'bg-blue-100 text-blue-600'
  }, {
    id: 2,
    title: 'Курс «Натальная карта для начинающих»',
    type: 'Видеокурс',
    price: '4 900 ₽',
    icon: PlayCircle,
    color: 'bg-purple-100 text-purple-600'
  }, {
    id: 3,
    title: 'Астро-календарь на 2025 год',
    type: 'PDF + таблицы',
    price: '1 990 ₽',
    icon: BookOpen,
    color: 'bg-emerald-100 text-emerald-600'
  }];
  const reviews = [{
    id: 1,
    name: 'Мария К.',
    date: '12 мая 2024',
    service: 'Натальная карта',
    rating: 5,
    text: 'Елена — потрясающий специалист! Очень глубокий разбор, всё по полочкам. Никакой воды, только конкретика. Ушла с полным пониманием, куда двигаться дальше. Обязательно вернусь за соляром!'
  }, {
    id: 2,
    name: 'Алексей Д.',
    date: '3 мая 2024',
    service: 'Синастрия',
    rating: 5,
    text: 'Заказывали с женой синастрию. Елена очень тактично и профессионально разобрала наши сильные и слабые стороны как пары. Многое стало понятнее, появились конкретные инструменты для работы над отношениями. Рекомендую всем парам, даже если кажется что всё хорошо — узнаете много нового.'
  }, {
    id: 3,
    name: 'Ольга В.',
    date: '28 апр 2024',
    service: 'Соляр на год',
    rating: 4,
    text: 'Хороший прогноз, всё структурировано. Единственное — хотелось бы чуть больше деталей по финансовой сфере.'
  }, {
    id: 4,
    name: 'Дмитрий Р.',
    date: '15 апр 2024',
    service: 'Натальная карта',
    rating: 5,
    text: 'Первый раз обращался к астрологу и не пожалел. Елена объясняет простым языком, без эзотерического тумана. Получил чёткие рекомендации по карьере.'
  }, {
    id: 5,
    name: 'Наталья С.',
    date: '2 апр 2024',
    service: 'Детский гороскоп',
    rating: 5,
    text: 'Заказала разбор карты для дочери. Очень полезно — теперь лучше понимаю её характер и как поддержать в учёбе.'
  }];
  const faqs = [{
    id: '1',
    title: 'Как проходит видео-консультация?',
    content: 'Созваниваемся в Zoom/Google Meet. Я заранее готовлю карту. Во время звонка разбираем все аспекты, после — отправляю запись и рекомендации.'
  }, {
    id: '2',
    title: 'Что нужно для подготовки?',
    content: 'Дата, время и место рождения. Если время неизвестно — проведём ректификацию.'
  }, {
    id: '3',
    title: 'Можно ли перенести запись?',
    content: 'Да, не позднее чем за 24 часа. При отмене менее чем за сутки предоплата не возвращается.'
  }];
  const similarAstrologers = [{
    id: 1,
    name: 'Михаил Волков',
    rating: 4.8,
    reviews: 86,
    price: 2990,
    badge: 'PRO',
    avatar: 'bg-amber-100 text-amber-700'
  }, {
    id: 2,
    name: 'Анна Коваль',
    rating: 5.0,
    reviews: 214,
    price: 3900,
    badge: 'TOP',
    avatar: 'bg-emerald-100 text-emerald-700'
  }, {
    id: 3,
    name: 'Дмитрий Орлов',
    rating: 4.9,
    reviews: 152,
    price: 3900,
    badge: 'Featured',
    avatar: 'bg-blue-100 text-blue-700'
  }];
  const visibleReviews = showAllReviews ? reviews : reviews.slice(0, 3);
  return <div className="w-full bg-slate-50 pb-20 min-h-screen" data-id="element-21050">
      <div className="h-40 md:h-52 bg-gradient-to-r from-indigo-500 via-purple-500 to-fuchsia-500 w-full relative" data-id="element-21051">
        <div className="absolute inset-0 bg-black/10" data-id="element-21052" />
        <div className="absolute top-3 left-4 sm:left-6 lg:left-8 z-10" data-id="element-21053">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Астрологи',
          onClick: () => onNavigate('astrologers')
        }, {
          label: profileName
        }]} className="text-white/80 [&_span]:text-white [&_button]:text-white/80 [&_button:hover]:text-white [&_svg]:text-white/50" data-id="element-21054" />
        </div>
      </div>

      {shareToast && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-800 text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg animate-[fadeIn_0.2s_ease-out]" data-id="element-21055">
          Ссылка скопирована ✓
        </div>}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-20" data-id="element-21056">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-id="element-21057">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-8 space-y-5" data-id="element-21058">
            {/* Profile Header */}
            <SectionErrorBoundary sectionName="astrologer-hero" data-id="element-21059">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-visible" data-id="element-21060">
                <div className="px-4 sm:px-6" data-id="element-21061">
                  {/* Mobile layout */}
                  <div className="sm:hidden flex flex-col items-center pt-16" data-id="element-21062">
                    <div className="relative mb-3 -mt-28" data-id="element-21063">
                      <div className="w-24 h-24 rounded-2xl bg-white p-1 shadow-lg" data-id="element-21064">
                        {profile.avatarUrl ? <img src={profile.avatarUrl} alt={profileName} className="w-full h-full rounded-xl object-cover" data-id="element-21065" /> : <div className="w-full h-full rounded-xl bg-indigo-100 flex items-center justify-center text-3xl font-bold text-indigo-700" data-id="element-21066">
                            {profileInitials}
                          </div>}
                      </div>
                      <div className="absolute bottom-1 right-1 w-4 h-4 bg-emerald-500 border-[3px] border-white rounded-full z-20" title="Онлайн" data-id="element-21067" />
                    </div>
                    <div className="text-center mb-2" data-id="element-21068">
                      <h1 className="text-xl font-bold text-slate-900 flex items-center justify-center gap-1.5 mb-1" data-id="element-21069">
                        {profileName}
                        <BadgeCheck className="w-5 h-5 text-blue-500 flex-shrink-0" data-id="element-21070" />
                      </h1>
                      <div className="flex items-center justify-center gap-1.5 mb-2" data-id="element-21071">
                        <BadgePill badge="TOP" data-id="element-21072" />
                        <BadgePill badge="PRO" data-id="element-21073" />
                      </div>
                      <div className="flex items-center justify-center gap-2 text-xs text-slate-500" data-id="element-21074">
                        <span className="flex items-center text-emerald-600 font-medium" data-id="element-21075">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 animate-pulse" data-id="element-21076" />
                          Онлайн
                        </span>
                        <span className="text-slate-300" data-id="element-21077">•</span>
                        <span className="flex items-center" data-id="element-21078">
                          <Zap className="w-3 h-3 text-amber-500 mr-0.5" data-id="element-21079" />
                          ~15 мин
                        </span>
                        <span className="text-slate-300" data-id="element-21080">•</span>
                        <span className="flex items-center" data-id="element-21081">
                          <MapPin className="w-3 h-3 mr-0.5" data-id="element-21082" />
                          Москва
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 w-full mb-4" data-id="element-21083">
                      <button onClick={() => setIsFavorite(!isFavorite)} className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-medium transition-all ${isFavorite ? 'border-rose-200 bg-rose-50 text-rose-600' : 'border-slate-200 text-slate-600'}`} data-id="element-21084">
                        <Heart className={`w-4 h-4 ${isFavorite ? 'fill-rose-500 text-rose-500' : ''}`} data-id="element-21085" />
                        {isFavorite ? 'В избранном' : 'В избранное'}
                      </button>
                      <button onClick={handleShare} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600" data-id="element-21086">
                        <Share2 className="w-4 h-4" data-id="element-21087" /> Поделиться
                      </button>
                    </div>
                  </div>

                  {/* Desktop layout */}
                  <div className="hidden sm:block pt-20" data-id="element-21088">
                    <div className="flex items-end gap-4 -mt-28 mb-6" data-id="element-21089">
                      <div className="relative flex-shrink-0" data-id="element-21090">
                        <div className="w-28 h-28 rounded-2xl bg-white p-1 shadow-lg" data-id="element-21091">
                          {profile.avatarUrl ? <img src={profile.avatarUrl} alt={profileName} className="w-full h-full rounded-xl object-cover" data-id="element-21092" /> : <div className="w-full h-full rounded-xl bg-indigo-100 flex items-center justify-center text-4xl font-bold text-indigo-700" data-id="element-21093">
                              {profileInitials}
                            </div>}
                        </div>
                        <div className="absolute bottom-1 right-1 w-4 h-4 bg-emerald-500 border-[3px] border-white rounded-full z-20" title="Онлайн" data-id="element-21094" />
                      </div>
                      <div className="flex-1 min-w-0 pb-1" data-id="element-21095">
                        <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-21096">
                          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-1.5" data-id="element-21097">
                            {profileName}
                            <BadgeCheck className="w-5 h-5 text-blue-500 flex-shrink-0" data-id="element-21098" />
                          </h1>
                          <BadgePill badge="TOP" data-id="element-21099" />
                          <BadgePill badge="PRO" data-id="element-21100" />
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-500" data-id="element-21101">
                          <span className="flex items-center text-emerald-600 font-medium" data-id="element-21102">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 animate-pulse" data-id="element-21103" />
                            Онлайн
                          </span>
                          <span className="text-slate-300" data-id="element-21104">•</span>
                          <span className="flex items-center" data-id="element-21105">
                            <Zap className="w-3 h-3 text-amber-500 mr-0.5" data-id="element-21106" />
                            ~15 мин
                          </span>
                          <span className="text-slate-300" data-id="element-21107">•</span>
                          <span className="flex items-center" data-id="element-21108">
                            <MapPin className="w-3 h-3 mr-0.5" data-id="element-21109" />
                            Москва
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 pb-1 flex-shrink-0" data-id="element-21110">
                        <button onClick={() => setIsFavorite(!isFavorite)} className={`p-2 rounded-lg border transition-all ${isFavorite ? 'border-rose-200 bg-rose-50' : 'border-slate-200 hover:border-rose-200 hover:bg-rose-50'}`} title={isFavorite ? 'Убрать из избранного' : 'В избранное'} data-id="element-21111">
                          <Heart className={`w-4 h-4 ${isFavorite ? 'fill-rose-500 text-rose-500' : 'text-slate-400'}`} data-id="element-21112" />
                        </button>
                        <button onClick={handleShare} className="p-2 rounded-lg border border-slate-200 hover:border-slate-300 transition-all" title="Поделиться" data-id="element-21113">
                          <Share2 className="w-4 h-4 text-slate-400" data-id="element-21114" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Info Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-3 border-y border-slate-100 mb-4 text-center" data-id="element-21115">
                    <div data-id="element-21116">
                      <div className="flex items-center justify-center gap-1 mb-0.5" data-id="element-21117">
                        <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" data-id="element-21118" />
                        <span className="font-bold text-slate-900 text-sm" data-id="element-21119">
                          4.9
                        </span>
                      </div>
                      <span className="text-[10px] text-slate-500" data-id="element-21120">
                        124 отзыва
                      </span>
                    </div>
                    <div data-id="element-21121">
                      <div className="font-bold text-slate-900 text-sm mb-0.5" data-id="element-21122">
                        {profile.experience} лет
                      </div>
                      <span className="text-[10px] text-slate-500" data-id="element-21123">Опыт</span>
                    </div>
                    <div data-id="element-21124">
                      <div className="flex items-center justify-center gap-1 mb-0.5" data-id="element-21125">
                        <Globe className="w-3.5 h-3.5 text-slate-400" data-id="element-21126" />
                        <span className="font-bold text-slate-900 text-sm" data-id="element-21127">
                          {profile.languages.split(',').map(l => l.trim().substring(0, 2).toUpperCase()).join(' ')}
                        </span>
                      </div>
                      <span className="text-[10px] text-slate-500" data-id="element-21128">Языки</span>
                    </div>
                    <div data-id="element-21129">
                      <div className="flex items-center justify-center gap-1.5 mb-0.5" data-id="element-21130">
                        <Video className="w-3.5 h-3.5 text-indigo-500" data-id="element-21131" />
                        <MessageSquare className="w-3.5 h-3.5 text-emerald-500" data-id="element-21132" />
                      </div>
                      <span className="text-[10px] text-slate-500" data-id="element-21133">
                        Форматы
                      </span>
                    </div>
                  </div>

                  {/* Specialties */}
                  <div className="flex flex-wrap gap-1.5 mb-5" data-id="element-21134">
                    {profile.specializations.map(tag => <span key={tag} className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium" data-id="element-21135">
                        {tag}
                      </span>)}
                  </div>

                  {/* About — NO CONTACTS BLOCK HERE */}
                  <div className="pb-6" data-id="element-21136">
                    <h3 className="text-base font-bold text-slate-900 mb-2" data-id="element-21137">
                      Обо мне
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed" data-id="element-21138">
                      {profile.aboutText}
                    </p>
                  </div>
                </div>
              </div>
            </SectionErrorBoundary>

            {/* Statistics */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3" data-id="element-21139">
              {[{
              icon: Users,
              value: '1 250+',
              label: 'Консультаций',
              color: 'bg-indigo-100 text-indigo-600'
            }, {
              icon: Heart,
              value: '94%',
              label: 'Возвращаются',
              color: 'bg-emerald-100 text-emerald-600'
            }, {
              icon: Clock,
              value: '~15 мин',
              label: 'Время ответа',
              color: 'bg-amber-100 text-amber-600'
            }].map(stat => <Card key={stat.label} padding="sm" className="flex flex-col sm:flex-row items-center sm:items-center gap-1.5 sm:gap-2.5 text-center sm:text-left" data-id="element-21140">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${stat.color}`} data-id="element-21141">
                    <stat.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-21142" />
                  </div>
                  <div data-id="element-21143">
                    <div className="text-sm sm:text-base font-bold text-slate-900 leading-tight" data-id="element-21144">
                      {stat.value}
                    </div>
                    <div className="text-[9px] sm:text-[10px] text-slate-500 font-medium" data-id="element-21145">
                      {stat.label}
                    </div>
                  </div>
                </Card>)}
            </div>

            {/* Services */}
            <SectionErrorBoundary sectionName="astrologer-services" data-id="element-21146">
              <Card padding="md" data-id="element-21147">
                <h2 className="text-lg font-bold text-slate-900 mb-3" data-id="element-21148">
                  Услуги
                </h2>
                <div className="divide-y divide-slate-100" data-id="element-21149">
                  {(showAllServices ? services : services.slice(0, 3)).map((s, i) => <div key={i} className="py-3 first:pt-0 last:pb-0 group" data-id="element-21150">
                        <div className="hidden sm:flex items-center gap-3" data-id="element-21151">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${s.format === 'video' ? 'bg-indigo-50' : 'bg-emerald-50'}`} data-id="element-21152">
                            {s.format === 'video' ? <Video className="w-4 h-4 text-indigo-500" data-id="element-21153" /> : <MessageSquare className="w-4 h-4 text-emerald-500" data-id="element-21154" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-21155">
                            <div className="text-sm font-bold text-slate-900 truncate group-hover:text-indigo-600 transition-colors" data-id="element-21156">
                              {s.title}
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-slate-500" data-id="element-21157">
                              <span className="flex items-center" data-id="element-21158">
                                <Clock className="w-3 h-3 mr-0.5" data-id="element-21159" />
                                {s.duration}
                              </span>
                              <span data-id="element-21160">•</span>
                              <span data-id="element-21161">
                                {s.format === 'video' ? 'Видео' : 'Чат'}
                              </span>
                            </div>
                          </div>
                          <span className="text-sm font-bold text-indigo-700 whitespace-nowrap" data-id="element-21162">
                            {s.price}
                          </span>
                          <Button variant="primary" size="sm" onClick={() => {
                      addItem({
                        id: `astrologer-service-${i}-${Date.now()}`,
                        name: `${s.title} — Елена Смирнова`,
                        description: s.desc,
                        price: parseInt(s.price.replace(/[^\d]/g, '')),
                        type: 'consultation',
                        format: s.format === 'video' ? 'Видеозвонок' : 'Чат',
                        duration: s.duration,
                        badges: [{
                          label: 'Консультация',
                          variant: 'pro'
                        }]
                      });
                      onNavigate('checkout');
                    }} className="flex-shrink-0 text-xs px-3" data-id="element-21163">
                            Заказать
                          </Button>
                        </div>
                        <div className="sm:hidden" data-id="element-21164">
                          <div className="flex items-start gap-2.5 mb-2" data-id="element-21165">
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${s.format === 'video' ? 'bg-indigo-50' : 'bg-emerald-50'}`} data-id="element-21166">
                              {s.format === 'video' ? <Video className="w-3.5 h-3.5 text-indigo-500" data-id="element-21167" /> : <MessageSquare className="w-3.5 h-3.5 text-emerald-500" data-id="element-21168" />}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-21169">
                              <div className="text-sm font-bold text-slate-900" data-id="element-21170">
                                {s.title}
                              </div>
                              <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5" data-id="element-21171">
                                <span className="flex items-center" data-id="element-21172">
                                  <Clock className="w-3 h-3 mr-0.5" data-id="element-21173" />
                                  {s.duration}
                                </span>
                                <span data-id="element-21174">•</span>
                                <span data-id="element-21175">
                                  {s.format === 'video' ? 'Видео' : 'Чат'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between ml-[38px]" data-id="element-21176">
                            <span className="text-sm font-bold text-indigo-700" data-id="element-21177">
                              {s.price}
                            </span>
                            <Button variant="primary" size="sm" onClick={() => {
                        addItem({
                          id: `astrologer-service-${i}-${Date.now()}`,
                          name: `${s.title} — Елена Смирнова`,
                          description: s.desc,
                          price: parseInt(s.price.replace(/[^\d]/g, '')),
                          type: 'consultation',
                          format: s.format === 'video' ? 'Видеозвонок' : 'Чат',
                          duration: s.duration,
                          badges: [{
                            label: 'Консультация',
                            variant: 'pro'
                          }]
                        });
                        onNavigate('checkout');
                      }} className="text-xs px-3" data-id="element-21178">
                              Заказать
                            </Button>
                          </div>
                        </div>
                      </div>)}
                </div>
                {services.length > 3 && <button onClick={() => setShowAllServices(!showAllServices)} className="w-full mt-3 pt-3 border-t border-slate-100 text-sm font-medium text-indigo-600 hover:text-indigo-700 flex items-center justify-center gap-1 transition-colors" data-id="element-21179">
                    {showAllServices ? 'Свернуть' : `Показать все ${services.length} услуг`}
                    <ChevronDown className={`w-4 h-4 transition-transform ${showAllServices ? 'rotate-180' : ''}`} data-id="element-21180" />
                  </button>}
              </Card>
            </SectionErrorBoundary>

            {/* Products */}
            <Card padding="md" data-id="element-21181">
              <div className="flex items-center justify-between mb-3" data-id="element-21182">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-1.5" data-id="element-21183">
                  <ShoppingBag className="w-4 h-4 text-indigo-500" data-id="element-21184" />
                  Товары
                </h2>
                <button className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center" onClick={() => onNavigate('product-detail')} data-id="element-21185">
                  Все <ChevronRight className="w-3 h-3 ml-0.5" data-id="element-21186" />
                </button>
              </div>
              <div className="divide-y divide-slate-100" data-id="element-21187">
                {products.map(p => <div key={p.id} className="flex items-center gap-2.5 py-2.5 first:pt-0 last:pb-0 cursor-pointer hover:bg-slate-50 rounded-lg transition-colors" onClick={() => onNavigate('product-detail')} data-id="element-21188">
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${p.color}`} data-id="element-21189">
                      <p.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-21190" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-21191">
                      <div className="text-xs sm:text-sm font-medium text-slate-900 truncate" data-id="element-21192">
                        {p.title}
                      </div>
                      <span className="text-[10px] text-slate-400 uppercase" data-id="element-21193">
                        {p.type}
                      </span>
                    </div>
                    <span className="text-xs sm:text-sm font-bold text-slate-900 whitespace-nowrap flex-shrink-0" data-id="element-21194">
                      {p.price}
                    </span>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" data-id="element-21195" />
                  </div>)}
              </div>
            </Card>

            {/* Education */}
            <Card padding="md" data-id="element-21196">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-21197">
                <GraduationCap className="w-5 h-5 text-indigo-500" data-id="element-21198" />
                Образование
              </h2>
              <div className="space-y-3" data-id="element-21199">
                {[{
                title: 'Московская Академия Астрологии',
                desc: 'Базовый и продвинутый курс • 2016-2018'
              }, {
                title: 'Школа Прогностики',
                desc: 'Глубокая прогностика и соляры • 2019'
              }].map((edu, i) => <div key={i} className="flex gap-3 items-start" data-id="element-21200">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-21201">
                      <Award className="w-4 h-4 text-slate-400" data-id="element-21202" />
                    </div>
                    <div data-id="element-21203">
                      <h4 className="text-sm font-bold text-slate-900" data-id="element-21204">
                        {edu.title}
                      </h4>
                      <p className="text-xs text-slate-500" data-id="element-21205">{edu.desc}</p>
                    </div>
                  </div>)}
              </div>
            </Card>

            {/* Reviews */}
            <SectionErrorBoundary sectionName="astrologer-reviews" data-id="element-21206">
              <Card padding="md" data-id="element-21207">
                <div className="flex items-center justify-between mb-4" data-id="element-21208">
                  <h2 className="text-lg font-bold text-slate-900" data-id="element-21209">
                    Отзывы{' '}
                    <span className="text-slate-400 text-sm font-normal" data-id="element-21210">
                      (124)
                    </span>
                  </h2>
                  <div className="flex items-center gap-1.5 bg-amber-50 px-2 py-1 rounded-lg" data-id="element-21211">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" data-id="element-21212" />
                    <span className="text-sm font-bold text-amber-700" data-id="element-21213">
                      4.9
                    </span>
                  </div>
                </div>
                <div className="space-y-4" data-id="element-21214">
                  {visibleReviews.map(r => <div key={r.id} className="border-b border-slate-100 pb-4 last:border-0 last:pb-0" data-id="element-21215">
                      <div className="flex items-start justify-between mb-2" data-id="element-21216">
                        <div className="flex items-center gap-2.5" data-id="element-21217">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-xs font-bold text-indigo-700" data-id="element-21218">
                            {r.name[0]}
                          </div>
                          <div data-id="element-21219">
                            <div className="text-sm font-bold text-slate-900" data-id="element-21220">
                              {r.name}
                            </div>
                            <div className="text-[10px] text-slate-500" data-id="element-21221">
                              {r.date} • {r.service}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5" data-id="element-21222">
                          {Array.from({
                        length: 5
                      }).map((_, i) => <Star key={i} className={`w-3 h-3 ${i < r.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-21223" />)}
                        </div>
                      </div>
                      <ReviewText text={r.text} maxLength={120} data-id="element-21224" />
                    </div>)}
                </div>
                {reviews.length > 3 && <Button variant="outline" size="sm" fullWidth className="mt-4" onClick={() => setShowAllReviews(!showAllReviews)} data-id="element-21225">
                    {showAllReviews ? 'Свернуть' : `Показать ещё ${reviews.length - 3} отзывов`}
                  </Button>}
              </Card>
            </SectionErrorBoundary>

            {/* FAQ */}
            <Card padding="md" data-id="element-21226">
              <h2 className="text-lg font-bold text-slate-900 mb-4" data-id="element-21227">
                Частые вопросы
              </h2>
              <Accordion items={faqs} data-id="element-21228" />
            </Card>
          </div>

          {/* RIGHT COLUMN: SIDEBAR */}
          <div className="lg:col-span-4" data-id="element-21229">
            <div className="sticky top-20 space-y-4" data-id="element-21230">
              {/* Booking Widget */}
              <Card padding="md" className="border-indigo-100 shadow-lg shadow-indigo-900/5" data-id="element-21231">
                <div className="flex items-center gap-3 mb-3" data-id="element-21232">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm flex-shrink-0" data-id="element-21233">
                    {profileInitials}
                  </div>
                  <div className="min-w-0" data-id="element-21234">
                    <h3 className="text-base font-bold text-slate-900 truncate" data-id="element-21235">
                      Консультация
                    </h3>
                    <p className="text-xs text-slate-500 truncate" data-id="element-21236">
                      {profileName}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mb-3 pb-3 border-b border-slate-100" data-id="element-21237">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-bold border border-indigo-100" data-id="element-21238">
                    <Video className="w-3 h-3" data-id="element-21239" /> Видеозвонок
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold" data-id="element-21240">
                    <Clock className="w-3 h-3" data-id="element-21241" /> 60 минут
                  </span>
                  <span className="ml-auto text-sm font-bold text-slate-900" data-id="element-21242">
                    от 2 990 ₽
                  </span>
                </div>
                <p className="text-xs text-slate-500 mb-3 font-medium" data-id="element-21243">
                  Выберите дату (МСК)
                </p>
                <div className="flex justify-between items-center mb-3 bg-slate-50 rounded-lg px-2 py-1.5" data-id="element-21244">
                  <button onClick={handlePrevMonth} className="p-0.5 text-slate-400 hover:text-slate-900 transition-colors" data-id="element-21245">
                    <ChevronDown className="w-4 h-4 rotate-90" data-id="element-21246" />
                  </button>
                  <span className="text-sm font-bold text-slate-900" data-id="element-21247">
                    {monthNames[currentMonth]} {currentYear}
                  </span>
                  <button onClick={handleNextMonth} className="p-0.5 text-slate-400 hover:text-slate-900 transition-colors" data-id="element-21248">
                    <ChevronDown className="w-4 h-4 -rotate-90" data-id="element-21249" />
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-1 mb-4" data-id="element-21250">
                  {availableDays.map((day, i) => <div key={day.num} onClick={() => setSelectedDay(i)} className={`text-center py-2 px-1 rounded-lg border cursor-pointer transition-all ${i === selectedDay ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 hover:border-indigo-200 text-slate-600'}`} data-id="element-21251">
                      <div className="text-[9px] uppercase tracking-wider font-semibold" data-id="element-21252">
                        {day.label}
                      </div>
                      <div className="font-bold text-base" data-id="element-21253">{day.num}</div>
                    </div>)}
                </div>
                <div className="mb-5" data-id="element-21254">
                  <div className="text-xs font-bold text-slate-900 mb-2 flex items-center" data-id="element-21255">
                    <Clock className="w-3.5 h-3.5 mr-1 text-slate-400" data-id="element-21256" />
                    Время:
                  </div>
                  <div className="grid grid-cols-3 gap-1.5" data-id="element-21257">
                    {timeSlots.map((time, i) => <button key={time} onClick={() => setSelectedTime(i)} className={`py-2 border rounded-lg text-sm font-medium transition-all ${i === selectedTime ? 'border-indigo-500 bg-indigo-500 text-white shadow-sm' : 'border-slate-200 text-slate-700 hover:border-indigo-300'}`} data-id="element-21258">
                        {time}
                      </button>)}
                  </div>
                </div>
                <Button variant="primary" size="lg" fullWidth onClick={() => {
                addItem({
                  id: `astrologer-booking-${Date.now()}`,
                  name: `Консультация — ${profileName}`,
                  description: `Видеозвонок 60 мин, ${availableDays[selectedDay]?.label} ${availableDays[selectedDay]?.num} ${monthNames[currentMonth]}, ${timeSlots[selectedTime]}`,
                  price: 2990,
                  type: 'consultation',
                  format: 'Видеозвонок',
                  duration: '60 минут',
                  badges: [{
                    label: 'Консультация',
                    variant: 'pro'
                  }]
                });
                onNavigate('checkout');
              }} data-id="element-21259">
                  Продолжить оформление
                </Button>
                <div className="mt-3 flex items-center justify-center text-[11px] font-medium text-slate-500 bg-slate-50 py-1.5 rounded-lg" data-id="element-21260">
                  <ShieldCheck className="w-3.5 h-3.5 mr-1 text-emerald-500" data-id="element-21261" />
                  Безопасная сделка
                </div>
              </Card>

              {/* Location */}
              <Card padding="sm" className="bg-slate-50 border-none" data-id="element-21262">
                <div className="flex items-center gap-2.5" data-id="element-21263">
                  <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-21264">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" data-id="element-21265" />
                  </div>
                  <div data-id="element-21266">
                    <div className="text-sm font-bold text-slate-900" data-id="element-21267">
                      Москва
                    </div>
                    <div className="text-[11px] text-slate-500" data-id="element-21268">
                      Онлайн по всему миру
                    </div>
                  </div>
                </div>
              </Card>

              {/* Contacts — Platform-safe */}
              <Card padding="sm" data-id="element-21269">
                <h3 className="text-sm font-bold text-slate-900 mb-3" data-id="element-21270">
                  Связаться
                </h3>
                <div className="space-y-2" data-id="element-21271">
                  <Button variant="primary" size="sm" fullWidth icon={<MessageSquare className="w-4 h-4" data-id="element-21273" />} onClick={() => onNavigate('consultation')} data-id="element-21272">
                    Написать в чат
                  </Button>
                  <Button variant="outline" size="sm" fullWidth icon={<Video className="w-4 h-4" data-id="element-21275" />} onClick={() => onNavigate('consultation')} data-id="element-21274">
                    Записаться на видео
                  </Button>
                  <div className="pt-3 mt-2 border-t border-slate-100" data-id="element-21276">
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mb-2" data-id="element-21277">
                      Соцсети
                    </div>
                    <div className="flex gap-2" data-id="element-21278">
                      <a href="https://t.me/elena_astro" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center text-[9px] font-bold text-sky-600 hover:bg-sky-100 transition-colors" title="Telegram" data-id="element-21279">
                        TG
                      </a>
                      <a href="https://instagram.com/elena_stars" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-pink-50 border border-pink-100 flex items-center justify-center text-[9px] font-bold text-pink-600 hover:bg-pink-100 transition-colors" title="Instagram" data-id="element-21280">
                        IG
                      </a>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-2" data-id="element-21281">
                      Все заказы и оплата — только через платформу Astriqo
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>

        {/* Ad Banner */}
        <div className="mt-8" data-id="element-21282">
          <AdSlot slotId="SLOT-12" onNavigate={onNavigate} data-id="element-21283" />
        </div>

        {/* Similar Astrologers */}
        <div className="mt-12 border-t border-slate-200 pt-8" data-id="element-21284">
          <div className="flex items-center justify-between mb-5" data-id="element-21285">
            <h2 className="text-lg font-bold text-slate-900" data-id="element-21286">
              Похожие астрологи
            </h2>
            <button onClick={() => onNavigate('astrologers')} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium hidden sm:flex items-center" data-id="element-21287">
              Все <ArrowRight className="w-3 h-3 ml-0.5" data-id="element-21288" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-21289">
            {similarAstrologers.map(a => <Card key={a.id} padding="sm" hoverable onClick={() => onNavigate('astrologer-profile')} data-id="element-21290">
                <div className="flex items-center gap-3" data-id="element-21291">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${a.avatar}`} data-id="element-21292">
                    {a.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-21293">
                    <div className="flex items-center gap-1.5 mb-0.5" data-id="element-21294">
                      <span className="text-sm font-bold text-slate-900 truncate" data-id="element-21295">
                        {a.name}
                      </span>
                      <BadgePill badge={a.badge} data-id="element-21296" />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500" data-id="element-21297">
                      <span className="flex items-center gap-0.5" data-id="element-21298">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-21299" />
                        {a.rating}
                      </span>
                      <span data-id="element-21300">от {a.price.toLocaleString('ru-RU')} ₽</span>
                    </div>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </div>
    </div>;
}